import React, { useEffect, useState } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ITicket, ITicketType, ITicketTypeOption, TicketStatus, PaymentStatus } from 'types/tickets';
import { IUser } from "../../types/users.d";
import { IMember } from 'types/members';
import { IEvent } from 'types/events.d';
import ticketTypesService from 'services/ticketTypes';
import EventSelect from 'components/EventSelect';
import MemberSelect from 'components/MemberSelect';

interface TicketFormProps {
  ticket: Partial<ITicket>;
  setTicket: (ticket: Partial<ITicket>) => void;
  user: IUser;
  rsvp?: any;
  event?: IEvent;
  member?: IMember;
}

// Define a type for the selectedOptions in ITicket
interface TicketSelectedOption {
  ticketTypeOption: ITicketTypeOption;
  value: any;
}

// Define Zod schema for form validation
const ticketFormSchema = z.object({
  event: z.any().refine(val => !!val, { message: "Event is required" }),
  member: z.any().refine(val => !!val, { message: "Member is required" }),
  ticketType: z.any().refine(val => !!val, { message: "Ticket type is required" }),
  attendeeInfo: z.object({
    email: z.string().email({ message: "Valid email is required" }).min(1, { message: "Email is required" }),
    name: z.string().min(1, { message: "Name is required" }),
    phone: z.string().optional().default("")
  }).optional().refine(val => !!val, { message: "Attendee information is required" }),
  organization: z.any().optional(),
  rsvp: z.any().optional(),
  price: z.number().optional(),
  currency: z.string().optional(),
  paymentStatus: z.enum(['free', 'pending', 'paid', 'refunded']).optional(),
  status: z.enum(['valid', 'used', 'cancelled']).optional(),
  selectedOptions: z.record(z.string(), z.object({
    value: z.any()
  })).optional()
});

type TicketFormSchema = z.infer<typeof ticketFormSchema>;

const TicketForm: React.FC<TicketFormProps> = ({ ticket, setTicket, user, rsvp, member }) => {
  const [ticketTypes, setTicketTypes] = useState<ITicketType[]>([]);
  const [loading, setLoading] = useState(false);
  const [ticketTypeOptions, setTicketTypeOptions] = useState<ITicketTypeOption[]>([]);

  // Transform ticket object to match form structure
  const transformTicketToFormValues = (): TicketFormSchema => {
    if (!ticket) return {} as TicketFormSchema;

    // Create a new object without the selectedOptions property
    const { selectedOptions, ...ticketData } = ticket;
    const formValues: Partial<TicketFormSchema> = {
      ...ticketData,
      event: ticket.event || null,
      member: ticket.member || null,
      ticketType: ticket.ticketType ? (typeof ticket.ticketType === 'string' ? ticket.ticketType : ticket.ticketType.id) : null,
      attendeeInfo: ticket.attendeeInfo || { name: '', email: '', phone: '' },
      status: ticket.status || 'valid',
      paymentStatus: ticket.paymentStatus || 'free',
      currency: ticket.currency || 'USD',
      price: ticket.price || 0
    };

    // Transform selectedOptions array to object format if it exists
    if (selectedOptions && Array.isArray(selectedOptions)) {
      formValues.selectedOptions = selectedOptions.reduce((acc, option) => {
        if (option.ticketTypeOption) {
          // Handle both object and string ID cases for ticketTypeOption
          const optionId = typeof option.ticketTypeOption === 'string'
            ? option.ticketTypeOption
            : option.ticketTypeOption.id;

          acc[optionId] = { value: option.value };
        }
        return acc;
      }, {} as TicketFormSchema['selectedOptions']);
    }

    console.log("Transformed form values:", formValues); // For debugging
    return formValues as TicketFormSchema;
  };

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
    register,
    trigger,
    reset
  } = useForm<TicketFormSchema>({
    resolver: zodResolver(ticketFormSchema),
    defaultValues: transformTicketToFormValues(),
    mode: 'onChange'
  });

  // Add effect to update form when ticket changes
  useEffect(() => {
    console.log("Ticket changed:", ticket);

    if (ticket.selectedOptions && Array.isArray(ticket.selectedOptions)) {
      console.log("Ticket has selectedOptions:", ticket.selectedOptions);

      // Log each option in detail
      ticket.selectedOptions.forEach(option => {
        console.log("Option:", {
          id: typeof option.ticketTypeOption === 'string' ? option.ticketTypeOption : option.ticketTypeOption?.id,
          value: option.value,
          originalOption: option.ticketTypeOption
        });
      });
    }

    const formValues = transformTicketToFormValues();
    console.log("Transformed form values:", formValues);

    if (formValues.selectedOptions) {
      console.log("Form selectedOptions:", formValues.selectedOptions);
    }

    reset(formValues);
  }, [ticket, reset]);

  const selectedEvent = watch('event');
  const selectedTicketTypeId = watch('ticketType');

  // Initialize form with URL parameters and defaults
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const eventId = searchParams.get('eventId');
    const rsvpId = searchParams.get('rsvpId');
    const memberId = searchParams.get('memberId');

    if (eventId && !ticket.event) {
      setValue('event', eventId as any);
    }

    if (rsvpId && !ticket.rsvp) {
      setValue('rsvp', rsvpId as any);
    }

    if (!ticket.id) {
      setValue('organization', user?.currentOrganization);
    }

    if (!ticket.id && rsvp) {
      setValue('rsvp', rsvp);
    }

    if (memberId && !ticket.member) {
      setValue('member', memberId as any);
    }
  }, [user, rsvp, setValue, ticket, member]);

  // Fetch ticket types when event changes
  useEffect(() => {
    const fetchTicketTypes = async () => {
      if (!selectedEvent) return;

      try {
        setLoading(true);
        const eventId = typeof selectedEvent === 'string' ? selectedEvent : selectedEvent._id;
        const response = await ticketTypesService.fetchByEvent(eventId);
        setTicketTypes(response);
      } catch (error) {
        console.error('Error fetching ticket types:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTicketTypes();
  }, [selectedEvent]);

  // Fetch ticket type options when ticket type changes
  useEffect(() => {
    const fetchTicketTypeOptions = async () => {
      if (!selectedTicketTypeId) return;

      try {
        setLoading(true);
        const ticketTypeId = typeof selectedTicketTypeId === 'string'
          ? selectedTicketTypeId
          : (selectedTicketTypeId as ITicketType).id;

        console.log("Fetching options for ticket type:", ticketTypeId);

        // Find the selected ticket type
        const foundTicketType = ticketTypes.find(type =>
          type.id === ticketTypeId || type.id === (selectedTicketTypeId as ITicketType)?.id
        );

        if (foundTicketType) {
          // If options are already included in the ticket type
          if (foundTicketType.ticketTypeOptions && Array.isArray(foundTicketType.ticketTypeOptions)) {
            console.log("Using embedded ticket type options:", foundTicketType.ticketTypeOptions);
            setTicketTypeOptions(foundTicketType.ticketTypeOptions as ITicketTypeOption[]);
          } else {
            // Otherwise fetch them from the API
            console.log("Fetching ticket type options from API");
            const options = await ticketTypesService.fetchOptions(ticketTypeId);
            console.log("Fetched ticket type options:", options);
            setTicketTypeOptions(options);
          }

          // Update the price based on the ticket type
          setValue('price', foundTicketType.price);
        }
      } catch (error) {
        console.error('Error fetching ticket type options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTicketTypeOptions();
  }, [selectedTicketTypeId, ticketTypes, setValue]);

  // Clear selectedOptions when ticket type options change
  useEffect(() => {
    // If there are no ticket type options, clear the selectedOptions
    if (ticketTypeOptions.length === 0) {
      setValue('selectedOptions', undefined);
    }
  }, [ticketTypeOptions, setValue]);

  // Debug log for selected options whenever they change
  const selectedOptionsValue = watch('selectedOptions');
  useEffect(() => {
    console.log("Current selectedOptions in form:", selectedOptionsValue);
    console.log("Current ticketTypeOptions:", ticketTypeOptions);
  }, [selectedOptionsValue, ticketTypeOptions]);

  // Update the parent component's state when form changes
  const onFormChange: SubmitHandler<TicketFormSchema> = (data) => {
    // Trigger validation
    trigger();

    console.log("Form data on change:", data);

    // Convert string ticketType to object if needed before saving
    let ticketTypeObj: ITicketType | undefined;

    if (typeof data.ticketType === 'string') {
      ticketTypeObj = ticketTypes.find(type => type.id === data.ticketType) as ITicketType | undefined;
    } else {
      ticketTypeObj = data.ticketType as ITicketType | undefined;
    }

    // Convert selectedOptions from object format to array format for ITicket
    const options: TicketSelectedOption[] = [];

    if (data.selectedOptions) {
      Object.entries(data.selectedOptions).forEach(([key, item]) => {
        const ticketTypeOption = ticketTypeOptions.find(option => option.id === key);
        if (ticketTypeOption) {
          options.push({
            ticketTypeOption,
            value: item.value
          });
        }
      });
    }

    console.log("Transformed selectedOptions:", options);

    // Create the formatted data with the correct type for selectedOptions
    const formattedData = {
      ...data,
      ticketType: ticketTypeObj,
      status: (data.status || 'valid') as TicketStatus,
      paymentStatus: (data.paymentStatus || 'free') as PaymentStatus,
      selectedOptions: options as any
    } as Partial<ITicket>;

    setTicket(formattedData);
  };

  return (
    <form onChange={handleSubmit(onFormChange)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Event Selection */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Event <span className="text-red-500">*</span>
          </label>
          <Controller
            name="event"
            control={control}
            rules={{ required: 'Event is required' }}
            render={({ field }) => (
              <EventSelect
                value={field.value || null}
                onChange={(event) => {
                  field.onChange(event);
                  setValue('ticketType', undefined);
                }}
                returnIdOnly={false}
              />
            )}
          />
          {errors.event && (
            <p className="mt-1 text-sm text-red-600">{errors.event.message?.toString() || 'Event is required'}</p>
          )}
        </div>

        {/* Member Selection */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Member <span className="text-red-500">*</span>
          </label>
          <Controller
            name="member"
            control={control}
            rules={{ required: 'Member is required' }}
            render={({ field }) => (
              <MemberSelect
                value={field.value}
                onChange={(member) => {
                  field.onChange(member);
                }}
                returnIdOnly={false}
              />
            )}
          />
          {errors.member && (
            <p className="mt-1 text-sm text-red-600">{errors.member.message?.toString() || 'Member is required'}</p>
          )}
        </div>

        {/* Ticket Type Selection */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ticket Type <span className="text-red-500">*</span>
          </label>
          <Controller
            name="ticketType"
            control={control}
            rules={{ required: 'Ticket type is required' }}
            render={({ field }) => (
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                disabled={loading || !selectedEvent}
                onChange={(e) => {
                  field.onChange(e);
                  // Clear selectedOptions when ticket type changes
                  setValue('selectedOptions', undefined);
                }}
                value={typeof field.value === 'string' ? field.value : field.value?.id || ''}
              >
                <option value="">Select a ticket type</option>
                {ticketTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} - {type.price} USD
                  </option>
                ))}
              </select>
            )}
          />
          {errors.ticketType && (
            <p className="mt-1 text-sm text-red-600">{errors.ticketType.message?.toString() || 'Ticket type is required'}</p>
          )}
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <Controller
            name="status"
            control={control}
            defaultValue="valid"
            render={({ field }) => (
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                {...field}
              >
                <option value="valid">Valid</option>
                <option value="used">Used</option>
                <option value="cancelled">Cancelled</option>
              </select>
            )}
          />
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
          <Controller
            name="price"
            control={control}
            defaultValue={0}
            render={({ field }) => (
              <input
                type="number"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                {...field}
                onChange={(e) => field.onChange(Number(e.target.value))}
              />
            )}
          />
        </div>

        {/* Currency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
          <Controller
            name="currency"
            control={control}
            defaultValue="USD"
            render={({ field }) => (
              <input
                type="text"
                className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                {...field}
              />
            )}
          />
        </div>

        {/* Payment Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
          <Controller
            name="paymentStatus"
            control={control}
            defaultValue="free"
            render={({ field }) => (
              <select
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                {...field}
              >
                <option value="free">Free</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
              </select>
            )}
          />
        </div>
      </div>

      {/* Attendee Information Section */}
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Attendee Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Controller
              name="attendeeInfo.name"
              control={control}
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <input
                  type="text"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  {...field}
                />
              )}
            />
            {errors.attendeeInfo?.name && (
              <p className="mt-1 text-sm text-red-600">{errors.attendeeInfo.name.message?.toString() || 'Name is required'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <Controller
              name="attendeeInfo.email"
              control={control}
              render={({ field }) => (
                <input
                  type="email"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  {...field}
                />
              )}
            />
            {errors.attendeeInfo?.email && (
              <p className="mt-1 text-sm text-red-600">{errors.attendeeInfo.email.message?.toString() || 'Email is required'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <Controller
              name="attendeeInfo.phone"
              control={control}
              render={({ field }) => (
                <input
                  type="text"
                  className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                  {...field}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Ticket Type Options Section */}
      {ticketTypeOptions.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Options</h3>
          <div className="space-y-4">
            {ticketTypeOptions.map((option) => (
              <div key={option.id} className="border p-4 rounded-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {option.name} {option.required && <span className="text-red-500">*</span>}
                </label>
                <p className="text-sm text-gray-500 mb-2">{option.description}</p>

                {option.type === 'text' && (
                  <Controller
                    name={`selectedOptions.${option.id}.value` as any}
                    control={control}
                    defaultValue=""
                    rules={{ required: option.required ? `${option.name} is required` : false }}
                    render={({ field }) => (
                      <input
                        type="text"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        placeholder={option.description}
                        value={field.value || ''}
                        onChange={field.onChange}
                      />
                    )}
                  />
                )}

                {option.type === 'number' && (
                  <Controller
                    name={`selectedOptions.${option.id}.value` as any}
                    control={control}
                    defaultValue={0}
                    rules={{
                      required: option.required ? `${option.name} is required` : false
                    }}
                    render={({ field }) => (
                      <input
                        type="number"
                        className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        placeholder={option.description}
                        value={field.value || 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                )}

                {option.type === 'boolean' && (
                  <Controller
                    name={`selectedOptions.${option.id}.value` as any}
                    control={control}
                    defaultValue={false}
                    rules={{ required: option.required ? `${option.name} is required` : false }}
                    render={({ field }) => (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-500">
                          {option.price > 0 ? `+${option.price} USD` : ''}
                        </span>
                      </div>
                    )}
                  />
                )}

                {option.type === 'select' && (
                  <Controller
                    name={`selectedOptions.${option.id}.value` as any}
                    control={control}
                    defaultValue=""
                    rules={{ required: option.required ? `${option.name} is required` : false }}
                    render={({ field }) => {
                      // Debug logging for this specific field
                      console.log(`Option ${option.id} current value:`, field.value);
                      return (
                        <select
                          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                          value={field.value || ''}
                          onChange={field.onChange}
                        >
                          <option value="">Select an option</option>
                          {option.options?.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      );
                    }}
                  />
                )}

                {errors.selectedOptions?.[option.id]?.value && (
                  <p className="mt-1 text-sm text-red-600">
                    {String(errors.selectedOptions[option.id]?.value?.message) || `${option.name} is required`}
                  </p>
                )}

                {option.price > 0 && option.type !== 'boolean' && (
                  <p className="text-sm text-gray-500 mt-1">
                    Additional cost: +{option.price} USD
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </form>
  );
};

export default TicketForm; 